// src/components/TicketCard.js
import React, { useRef, useContext } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const TicketCard = ({ ticket, index, isOverdue, isStaticList = false, hasActiveSubTicket }) => {
   
    // --- HOOKS ARE NOW AT THE TOP LEVEL ---
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const isClient = user?.roles?.includes('Client');

    // This ref is used to distinguish between a click and a drag for draggable cards.
    const wasDragged = useRef(false);

    // --- SHARED JSX FOR BOTH CARD TYPES ---
    const cardContent = (
        <>
            <p className="ticket-card-subject">{ticket.subject}</p>
            
            {/* ✅ NEW: Sub Ticket Active Badge */}
            {hasActiveSubTicket && ticket.status !== 'In Progress' && (
                <div style={{ marginBottom: '0.5rem' }}>
                    <span className="status-badge status-sub-ticket-active" style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                        Sub Ticket Active
                    </span>
                </div>
            )}

            <div className="ticket-card-footer">
                <span className="ticket-card-client">{ticket.client}</span>
     
                {/* ✅ UPDATED: Hide Priority Tag if Client */}
                {!isClient && (
                    <span className={`tag priority-${(ticket.priority || 'low').toLowerCase()}`}>{ticket.priority}</span>
                )}
            </div>
        </>
    );

    // --- CONDITIONAL RENDERING ---

    // If this is for a static list (like the "Open" column), render a simple, non-draggable div.
    // It has a direct onClick handler for navigation.
    if (isStaticList) {
        return (
            <div
                onClick={() => navigate(`/tickets/${ticket._id}`)}
                className={`ticket-card ${isOverdue ? 'overdue' : ''}`}
                style={{ cursor: 'pointer' }}
            >
                {cardContent}
            </div>
        );
    }
    
    // --- LOGIC FOR DRAGGABLE CARDS ONLY ---
    // These handlers prevent navigation when a drag ends on the same element (a "click").
    const handleMouseDown = () => { wasDragged.current = false; };
    const handleMouseMove = () => { wasDragged.current = true; };
    const handleClick = () => { if (!wasDragged.current) { navigate(`/tickets/${ticket._id}`); } };

    // This is the draggable version for all other columns.
    return (
        <Draggable draggableId={String(ticket._id)} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onClick={handleClick}
                    className={`ticket-card ${snapshot.isDragging ? 'is-dragging' : ''} ${isOverdue ? 'overdue' : ''}`}
                    style={{
                        ...provided.draggableProps.style,
                        cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                    }}
                >
                    {cardContent}
                </div>
            )}
        </Draggable>
    );
};

export default TicketCard;